import { EpitBindable } from './Bindable';
import { config } from './Config';
import app from './App';
import debug from './Debug';
import debugRenderer from './DebugRenderer';
import util from './Util'
import translator from './Translator';
import navi from './Navi';

'use strict';

/**
 * Manages the user interface.
 *
 * Can fire the following events:
 *
 *    > READY - fired when ui is ready
 *    > MODAL_SHOWN - fired when modal window is displayed
 *        modal - the modal that was shown
 *    > MODAL_HIDDEN - fired when modal window is hidden
 *    > SWIPE - fired when document is swiped
 *        direction - direction of the swipe, one of left, right, top, bottom
 *        distance - x and y of swipe direction
 *        startPosition - start x, y
 *        endPosition    - end x, y
 *
 * @class UI
 * @extends EpitBindable
 * @constructor
 * @module Core
 */
export class EpitUI extends EpitBindable implements UI {
    constructor() {
        super();
    }


    /**
     * Event types.
     *
     * @event
     * @param {Object} Event
     * @param {String} Event.READY UI is ready
     * @param {String} Event.MODAL_SHOWN A modal window is displayed
     * @param {String} Event.MODAL_HIDDEN A modal window is hidden
     * @param {String} Event.SWIPE Document is swiped
     */
    Event = {
        READY: 'ready',
        MODAL_SHOWN: 'modal-shown',
        MODAL_HIDDEN: 'modal-hidden',
        SWIPE: 'swipe'
    };

    /**
     * Initializes the debugger.
     *
     * @method init
     * @return {UI} Self
     */
    init() {
        $(document).ready(() => {
            this._onDocumentReady();
        });

        return this;
    };

    /**
     * Opens a view in a modal window.
     *
     * @method openModal
     * @param {String} module Module to open
     * @param {String} [action=index] Action to navigate to
     * @param {Object} [parameters] Action parameters
     * @param {Object} [options] Optional modal options
     */
    openModal(component, parameters): Promise<any> {
        this.showModal('<div id="modal-content"></div>', () => {
            navi.partial('#modal-content', component, parameters);
        });

        return new Promise<any>((resolve, reject) => {
            let binding = this.bind(this.Event.MODAL_HIDDEN, (e) => {
                if (e.component === component) {
                    resolve();
                    binding.unbind();
                }
            });
        });
    };

    /**
     * Displays a modal window with specific content.
     *
     * @method showModal
     * @param {String} content Modal content
     * @param {Object} [options] Optional modal options
     * @param {Function} [readyCallback] Callback to call once modal is displayed
     */
    showModal(content, readyCallback) {
        let self = this,
            existing = $('#modal');

        // TODO Not sure if this is great..
        if (existing.length > 0) {
            this.closeModal();
            //existing.remove();
        }

        $(document.body).append(`<div id="modal" style="display: none;"></div>`);

        $('#modal')
            .html(content)
        //.on('shown', function () {
        //    self.fire(self.Event.MODAL_SHOWN);
        //})
        //.on('hidden', function () {
        //    $(this).remove();
        //    self.fire(self.Event.MODAL_HIDDEN);
        //})
        ;

        if (util.isFunction(readyCallback)) {
            readyCallback();
        }
    };

    closeModal() {
        $.fancybox.close();
    };

    /**
     * Displays partial content.
     *
     * @method showPartial
     * @param {String} module Name of the module
     * @param {String} action Name of the action
     * @param {String} className Class name of the module
     * @param {String} actionName Method name of the action
     * @param {Array} parameters Action parameters
     * @param {Object} moduleObj Module object
     * @param {String} viewContent View content to render
     * @param {String} containerSelector Container selector to place the content into
     * @param {Boolean} append Should the content be appended instead of replaced
     * @private
     */
    showPartial(component, parameters, containerSelector) {
        let container = $(containerSelector);

        if (container.length === 0)
            throw new Error('Partial container for "' + containerSelector + '" not found');

        // cleanup
        var oldContents = container.children()[0];
        if (oldContents && !!angular.element(oldContents).scope)
            angular.element(oldContents).scope().$destroy();
        container.empty();

        try {
            container.append(app.compile(`<${component}></${component}>`)(container.scope().$new()));
        } catch (e) {
            debug.error(e);
        }
    };

    // periodics hack, don't use for normal components
    replacePartialComponentHtml(componentSelector, newHtml) {
        let container = $(componentSelector);
        if (container.length === 0)
            throw new Error('Partial container for "' + componentSelector + '" not found');

        try {
            //console.log('BEFORE: ' + Object.keys(container.children().scope()).reduce((s, key)=>s + ';' + key));

            container.html($(newHtml).html());
            app.compile(container.contents())(container.children().scope());

            //console.log('AFTER: ' + Object.keys(container.children().scope()).reduce((s, key)=>s + ';' + key));
        } catch (e) {
            debug.error(e);
        }
    };


    /**
     * Called on document ready.
     *
     * @method _onDocumentReady
     * @private
     */
    private _onDocumentReady() {
        if (config.debug) {
            debugRenderer.init(this);

            app.debugRenderer = debugRenderer;
        }

        if (("ontouchstart" in document.documentElement)) {
            document.documentElement.className += " touch-enabled";
        }

        this.fire({
            type: this.Event.READY
        });

        this.initModal();
    };

    private initModal() {
        $.fancybox.init();

        $("body").on("click", ".modal-window-close", (e) => {
            $('.ui-tooltip').remove();
        });

        navi.bind(navi.Event.POST_PARTIAL, (e) => {
            if (e.containerSelector != '#modal-content')
                return;

            var containerSelector = '#' + e.component;
            if (!$(containerSelector).length)
                throw new Error('Modal container not found by id: ' + e.component);

            let application = navi.getParameter('application');
            application = !!application && e.component.startsWith(application.toLowerCase()) ? application.toLowerCase() : '';

            let modalStyleClass = (e.parameters || {})['class'];

            let ui = this;

            var fancyConfig = {
                margin: [0, 0, 0, 160],
                padding: 0,
                openEffect: "fade",
                closeEffect: "fade",
                openSpeed: 200,
                closeSpeed: 200,
                scrolling: "visible",
                tpl: {
                    wrap: `
                        <div class="fancybox-wrap ${application} ${modalStyleClass}" tabIndex="-1"><div class="fancybox-skin"><div class="fancybox-outer"><div class="fancybox-inner"></div></div></div></div>
                    `,
                    error: '<p class="fancybox-error">Sisu pole võimalik laadida.<br>Proovi hiljem uuesti.</p>',
                    closeBtn: '<a class="button button-link button-icon-right modal-window-close" href="#">' + translator.translate('close') + '<i class="icon-cancel"></i></a>'
                },
                fitToView: false,
                autoCenter: false,
                autoResize: true,
                autoSize: true,
                topRatio: 0,
                leftRatio: 0,
                href: containerSelector,
                beforeLoad: () => {
                    $('body').addClass('no-scroll');
                },
                afterLoad: () => {
                    ui.fire(this.Event.MODAL_SHOWN);
                },
                afterClose: () => {
                    var oldContents = angular.element(containerSelector);
                    if (!!oldContents && !!oldContents.scope())
                        oldContents.scope().$destroy();
                    oldContents.empty();

                    $('body').removeClass('no-scroll');
                    $('.fancybox-overlay').hide();
                    ui.fire({type: ui.Event.MODAL_HIDDEN, component: e.component});
                },
                keys: null,
                helpers: null
            };

            let opts = {
                keys: {
                    close: [27], // escape key
                    play: [] // No binding, prevents entering slide-show mode when 'space' is pressed
                }
            };

            let container = $(containerSelector);
            if (container.hasClass('modal-protected')) {
                if (!container.hasClass('modal-closeable')) {
                    fancyConfig.tpl.closeBtn = null;
                    fancyConfig.keys = {close: null};
                }
                fancyConfig.helpers = {overlay: {closeClick: false}};
            }

            $.fancybox.open([fancyConfig], opts);
        });
    }
}


export default new EpitUI();


// WEBPACK FOOTER //
// ./src/ui/core/application/UI.ts